import { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleXmark,
  faMagnifyingGlass,
  faRotate,
  faScroll,
  faShieldHalved,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';

interface AuditEntry {
  timestamp?: string;
  createdAt?: string;
  action: string;
  details?: Record<string, unknown>;
  repoName?: string;
  success: boolean;
  error?: string;
  duration?: number;
  durationMs?: number;
}

interface AuditLogViewProps {
  inline?: boolean;
  onClose: () => void;
  history?: unknown[];
  auditLog: AuditEntry[];
  onRefresh?: () => void;
}

function parseDate(item: { createdAt?: string; timestamp?: string }): Date {
  const raw = item.createdAt || item.timestamp || '';
  return new Date(raw.replace(' ', 'T') + (item.createdAt ? 'Z' : ''));
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function getActionColor(action: string): string {
  if (action.includes('STARTED')) return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/20';
  if (action.includes('COMPLETED') || action.includes('GENERATED')) return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20';
  if (action.includes('FAILED')) return 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/20';
  if (action.includes('CACHED')) return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20';
  if (action.includes('CLEARED') || action.includes('DELETE')) return 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/20';
  return 'bg-gray-500/15 text-gray-700 dark:text-gray-300 border-gray-500/20';
}

export function AuditHistoryView({ inline, onClose: _onClose, auditLog, onRefresh }: AuditLogViewProps) {
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'failed'>('all');
  const [expandedAudit, setExpandedAudit] = useState<Set<number>>(new Set());
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const toggleAuditExpand = (idx: number) => {
    setExpandedAudit(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const auditStats = useMemo(() => {
    const total = auditLog.length;
    const successful = auditLog.filter(a => a.success).length;
    const failed = total - successful;
    const uniqueActions = new Set(auditLog.map(a => a.action)).size;
    return { total, successful, failed, uniqueActions };
  }, [auditLog]);

  const filteredAudit = useMemo(() => {
    return auditLog.filter(a => {
      if (filterStatus === 'success' && !a.success) return false;
      if (filterStatus === 'failed' && a.success) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (!a.action.toLowerCase().includes(q) && !(a.repoName || '').toLowerCase().includes(q)) return false;
      }
      return true;
    }).sort((a, b) => parseDate(b).getTime() - parseDate(a).getTime());
  }, [auditLog, filterSearch, filterStatus]);

  // Group audit by date
  const auditByDate = useMemo(() => {
    const groups = new Map<string, { entry: AuditEntry; originalIdx: number }[]>();
    for (let i = 0; i < filteredAudit.length; i++) {
      const entry = filteredAudit[i];
      const d = parseDate(entry);
      const key = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ entry, originalIdx: auditLog.indexOf(entry) });
    }
    return Array.from(groups.entries());
  }, [filteredAudit, auditLog]);

  return (
    <div className={`${inline ? 'h-[calc(100vh-4rem)]' : 'fixed inset-0 z-[100]'} flex flex-col overflow-hidden font-sans`} style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>

      {/* Header */}
      <div className={`flex-shrink-0 z-10 ${isDark ? 'bg-slate-950/60' : 'bg-white/50'} backdrop-blur-xl border-b ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDark ? 'bg-amber-500/[0.08]' : 'bg-amber-50'}`}>
              <FontAwesomeIcon icon={faShieldHalved} className={`h-5 w-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Audit Log</h1>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>System activity and event trail</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <button onClick={onRefresh} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-black/[0.04]'}`}>
                <FontAwesomeIcon icon={faRotate} className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className={`rounded-xl border p-4 backdrop-blur-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white/60 border-black/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faScroll} className={`h-4 w-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Events</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{auditStats.total}</p>
            </div>
            <div className={`rounded-xl border p-4 backdrop-blur-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white/60 border-black/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faCircleCheck} className={`h-4 w-4 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Successful</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{auditStats.successful}</p>
            </div>
            <div className={`rounded-xl border p-4 backdrop-blur-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white/60 border-black/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faCircleXmark} className={`h-4 w-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Errors</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>{auditStats.failed}</p>
            </div>
            <div className={`rounded-xl border p-4 backdrop-blur-xl ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white/60 border-black/[0.06]'}`}>
              <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faChartLine} className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Action Types</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{auditStats.uniqueActions}</p>
            </div>
          </div>

          {/* Search + status filter */}
          <div className="flex items-center gap-2 w-full">
            <div className="relative flex-1 max-w-sm">
              <FontAwesomeIcon icon={faMagnifyingGlass} className={`absolute left-3 top-2.5 h-3.5 w-3.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              <input type="text" value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
                placeholder="Search actions or repos..."
                className={`w-full pl-9 pr-3 py-2 rounded-xl text-xs border focus:outline-none focus:ring-2 focus:ring-blue-500/30 backdrop-blur-sm ${isDark ? 'bg-white/[0.04] border-white/[0.08] text-white placeholder-slate-500' : 'bg-white/80 border-black/[0.08] text-slate-900 placeholder-slate-400'}`} />
            </div>
            <div className={`inline-flex rounded-xl p-0.5 backdrop-blur-sm ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.04]'}`}>
              {(['all', 'success', 'failed'] as const).map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-colors capitalize ${filterStatus === s
                    ? (isDark ? 'bg-white/[0.08] text-white' : 'bg-white/80 text-slate-900 shadow-sm')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Audit entries */}
          <div className="space-y-4">
            {filteredAudit.length === 0 ? (
              <div className={`text-center py-16 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                <FontAwesomeIcon icon={faScroll} className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">No audit entries yet</p>
                <p className="text-xs mt-1">System events will appear here as you use the app</p>
              </div>
            ) : (
              auditByDate.map(([date, items]) => (
                <div key={date}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{date}</h3>
                  <div className="space-y-1.5">
                    {items.map(({ entry, originalIdx }) => {
                      const dur = entry.durationMs ?? entry.duration ?? 0;
                      const time = parseDate(entry).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                      const isExpanded = expandedAudit.has(originalIdx);
                      const hasDetails = entry.details && Object.keys(entry.details).length > 0;
                      return (
                        <div key={originalIdx}
                          className={`${isDark ? 'bg-white/[0.03] hover:bg-white/[0.05]' : 'bg-white/60 hover:bg-white/80'} backdrop-blur-xl border ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'} rounded-xl overflow-hidden transition-colors ${hasDetails || entry.error ? 'cursor-pointer' : ''}`}
                          onClick={() => (hasDetails || entry.error) && toggleAuditExpand(originalIdx)}>
                          <div className="px-4 py-3 flex items-center gap-3">
                            {/* Status dot */}
                            <div className={`flex-shrink-0 w-2 h-2 rounded-full ${entry.success ? 'bg-emerald-400' : 'bg-red-400'}`} />
                            {/* Action badge */}
                            <span className={`flex-shrink-0 text-[10px] font-mono px-2 py-0.5 rounded border ${getActionColor(entry.action)}`}>
                              {entry.action}
                            </span>
                            {/* Repo name */}
                            {entry.repoName && (
                              <span className={`text-xs font-medium truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{entry.repoName}</span>
                            )}
                            {/* Spacer */}
                            <div className="flex-1" />
                            {/* Duration */}
                            {dur > 0 && (
                              <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{formatDuration(dur)}</span>
                            )}
                            {/* Time */}
                            <span className={`text-[10px] flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{time}</span>
                            {/* Expand indicator */}
                            {(hasDetails || entry.error) && (
                              isExpanded ? <FontAwesomeIcon icon={faChevronDown} className={`h-3 w-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} /> : <FontAwesomeIcon icon={faChevronRight} className={`h-3 w-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                            )}
                          </div>
                          {/* Expanded content */}
                          {isExpanded && (
                            <div className={`px-4 pb-3 pt-0 border-t ${isDark ? 'border-white/[0.06]' : 'border-black/[0.06]'}`}>
                              {entry.error && (
                                <div className={`flex items-start gap-2 mt-2 p-2 rounded-lg ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
                                  <FontAwesomeIcon icon={faTriangleExclamation} className={`h-3.5 w-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                                  <p className={`text-xs ${isDark ? 'text-red-300' : 'text-red-700'}`}>{entry.error}</p>
                                </div>
                              )}
                              {hasDetails && (
                                <pre className={`mt-2 text-[10px] p-2.5 rounded-lg overflow-x-auto font-mono ${isDark ? 'bg-white/[0.02] border border-white/[0.04] text-slate-300' : 'bg-black/[0.02] border border-black/[0.04] text-slate-700'}`}>
                                  {JSON.stringify(entry.details, null, 2)}
                                </pre>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
